// Multi-tab cockpit state with localStorage persistence. Each tab carries
// its own RGL layout + list of hidden widget ids. The hook also handles
// the share-import flow: callers pass an imported preset and we add it as
// a new tab (per product decision: always-import, no preview-only mode).
//
// Storage shape (key = `tm.cockpit.${scope}.v1`):
//   {
//     version: 1,
//     activeTabId: string,
//     tabs: [{ id, name, layout: RGL.Layout[], hidden: string[], pinned?: boolean }]
//   }
//
// Default tab is pinned (cannot be deleted) so users can always get back
// to a clean slate without dealing with empty-state UI.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildDefaultLayout, WIDGET_REGISTRY, ALL_WIDGET_IDS, instanceTypeOf, makeInstanceId } from './widgetRegistry';
import { API_BASE_URL } from '../../utils/apiConfig';

const STORAGE_VERSION = 1;
const DEFAULT_TAB_ID = 'tab_default';
// Stable id format for server-controlled pinned tabs. Includes the slot
// index so admin reordering doesn't accidentally fuse two different tabs.
const SERVER_DEFAULT_ID_PREFIX = 'tab_default_';
const serverDefaultId = (slot) => `${SERVER_DEFAULT_ID_PREFIX}${slot}`;
const isServerDefault = (id) => typeof id === 'string' && id.startsWith(SERVER_DEFAULT_ID_PREFIX);

function makeId() {
  // Short non-cryptographic id — enough to avoid collisions inside a
  // single user's tab list (max few dozen tabs).
  return 'tab_' + Math.random().toString(36).slice(2, 9);
}

function buildDefaultState() {
  // Built-in fallback shown until the server-configured defaults arrive
  // (and used in dev when the admin hasn't published any). Replaced on
  // first successful fetch from /api/cockpit/default-tabs.
  return {
    version: STORAGE_VERSION,
    activeTabId: DEFAULT_TAB_ID,
    defaultsVersion: 0,
    tabs: [
      {
        id: DEFAULT_TAB_ID,
        name: 'Default',
        layout: buildDefaultLayout(),
        hidden: [],
        widgetSettings: {},
        pinned: true,
      },
    ],
  };
}

// Ensure every tab has the fields newer code expects. Lets us load tabs
// saved before `widgetSettings` was introduced without losing their layouts.
function normaliseTabs(tabs) {
  return tabs.map((t) => ({
    widgetSettings: {},
    hidden: [],
    ...t,
  }));
}

function load(scope) {
  try {
    const raw = localStorage.getItem(`tm.cockpit.${scope}.v${STORAGE_VERSION}`);
    if (!raw) return buildDefaultState();
    const parsed = JSON.parse(raw);
    if (parsed?.version !== STORAGE_VERSION || !Array.isArray(parsed.tabs) || parsed.tabs.length === 0) {
      return buildDefaultState();
    }
    return { ...parsed, tabs: normaliseTabs(parsed.tabs) };
  } catch {
    return buildDefaultState();
  }
}

function persist(scope, state) {
  try {
    localStorage.setItem(`tm.cockpit.${scope}.v${STORAGE_VERSION}`, JSON.stringify(state));
  } catch {
    // Storage full or disabled — silently degrade to in-memory only.
  }
}

// Reconcile server defaults into local tab list. Server owns the pinned
// tabs entirely — name, layout, hidden, settings all replace. User-created
// tabs (non-pinned) are untouched. activeTabId is preserved when still valid,
// otherwise points to first server default.
function mergeServerDefaults(state, serverTabs, serverVersion) {
  const userTabs = (state.tabs || []).filter((t) => !t.pinned && !isServerDefault(t.id));
  const pinned = serverTabs.map((t, slot) => ({
    id: serverDefaultId(slot),
    name: t.name,
    layout: t.layout || [],
    hidden: t.hidden || [],
    widgetSettings: t.widgetSettings || {},
    pinned: true,
  }));
  const tabs = [...pinned, ...userTabs];
  const valid = new Set(tabs.map((t) => t.id));
  const activeTabId = valid.has(state.activeTabId)
    ? state.activeTabId
    : (pinned[0]?.id || tabs[0]?.id || DEFAULT_TAB_ID);
  return { ...state, tabs, activeTabId, defaultsVersion: serverVersion };
}

export function useCockpitTabs(scope) {
  const [state, setState] = useState(() => load(scope));
  const stateRef = useRef(state);
  stateRef.current = state;

  // Debounced persist — RGL's onLayoutChange can fire 60Hz during a drag,
  // and localStorage.setItem is a synchronous JSON.stringify + main-thread
  // write. Coalesce to one write per 400ms idle, and flush on page hide
  // so nothing is lost if the user navigates away mid-edit.
  useEffect(() => {
    const timer = setTimeout(() => persist(scope, state), 400);
    return () => clearTimeout(timer);
  }, [scope, state]);

  // Pull server-configured default tabs on every mount. Admin can update
  // them at any time and changes propagate to all clients on next reload.
  // Short-circuits when the version hasn't changed since our last merge.
  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE_URL}/api/cockpit/default-tabs?scope=${encodeURIComponent(scope)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!alive || !data?.success) return;
        const serverTabs = Array.isArray(data.tabs) ? data.tabs : [];
        const serverVersion = data.version || 0;
        // Nothing published yet on this scope — keep the built-in fallback.
        if (serverTabs.length === 0) return;
        setState((s) => {
          if (s.defaultsVersion === serverVersion) return s;
          return mergeServerDefaults(s, serverTabs, serverVersion);
        });
      })
      .catch(() => { /* offline — built-in fallback already showing */ });
    return () => { alive = false; };
  }, [scope]);

  // Best-effort flush before the tab closes / backgrounds — covers the
  // case where a drag is in progress when the user switches tabs and the
  // debounced timer hasn't fired yet.
  useEffect(() => {
    const flush = () => persist(scope, stateRef.current);
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', flush);
    return () => {
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', flush);
    };
  }, [scope]);

  const activeTab = useMemo(
    () => state.tabs.find((t) => t.id === state.activeTabId) || state.tabs[0],
    [state]
  );

  const setActiveTab = useCallback((id) => {
    setState((s) => {
      if (!s.tabs.some((t) => t.id === id)) return s;
      return { ...s, activeTabId: id };
    });
  }, []);

  // Update layout for the currently active tab (called from RGL onLayoutChange).
  const updateActiveLayout = useCallback((layout) => {
    setState((s) => ({
      ...s,
      tabs: s.tabs.map((t) =>
        t.id === s.activeTabId ? { ...t, layout } : t
      ),
    }));
  }, []);

  const setHidden = useCallback((hidden) => {
    setState((s) => ({
      ...s,
      tabs: s.tabs.map((t) =>
        t.id === s.activeTabId ? { ...t, hidden } : t
      ),
    }));
  }, []);

  // Remove a widget — drops it from layout AND tracks it in `hidden` so the
  // legacy "Add widget" dropdown can offer it back. The Library panel
  // derives availability from the layout directly so it doesn't need `hidden`.
  const hideWidget = useCallback((widgetId) => {
    setState((s) => ({
      ...s,
      tabs: s.tabs.map((t) => {
        if (t.id !== s.activeTabId) return t;
        return {
          ...t,
          hidden: t.hidden.includes(widgetId) ? t.hidden : [...t.hidden, widgetId],
          layout: t.layout.filter((l) => l.i !== widgetId),
        };
      }),
    }));
  }, []);

  // Add a widget back to the layout. Uses the registry's default size; RGL's
  // vertical compactor positions it under existing rows so it never collides.
  const showWidget = useCallback((widgetId) => {
    setState((s) => ({
      ...s,
      tabs: s.tabs.map((t) => {
        if (t.id !== s.activeTabId) return t;
        if (t.layout.some((l) => l.i === widgetId)) return t;
        const def = WIDGET_REGISTRY[widgetId];
        const fallback = { i: widgetId, x: 0, y: Infinity, w: 4, h: 6 };
        const newItem = def?.defaultLayout
          ? { i: widgetId, ...def.defaultLayout, y: Infinity }
          : fallback;
        return {
          ...t,
          hidden: t.hidden.filter((id) => id !== widgetId),
          layout: [...t.layout, newItem],
        };
      }),
    }));
  }, []);

  // Soft cap on widget instances per tab. Chart instances each carry a
  // ~MB canvas + animation loop; somewhere around 15 the page starts
  // feeling heavy on mid-range laptops. We warn the user rather than
  // hard-blocking so power users can override if they know what they're
  // doing — but the warning is loud.
  const SOFT_CAP = 15;

  // Library entry-point. For singleton widgets (no `multi` flag) this works
  // as a toggle. For multi widgets it always *adds* a new instance with a
  // unique id — so clicking `Chart (All)` three times yields three independent
  // chart widgets the user can pin to different coins.
  const toggleWidget = useCallback((widgetType) => {
    setState((s) => {
      const tab = s.tabs.find((t) => t.id === s.activeTabId);
      if (!tab) return s;
      const def = WIDGET_REGISTRY[widgetType];
      if (!def) return s;

      if (def.multi) {
        if (tab.layout.length >= SOFT_CAP) {
          // eslint-disable-next-line no-console
          console.warn(`[cockpit] Soft cap of ${SOFT_CAP} widgets hit on tab "${tab.name}". Skipping add — remove a widget first.`);
          return s;
        }
        const newId = makeInstanceId(widgetType);
        const newItem = def.defaultLayout
          ? { i: newId, ...def.defaultLayout, y: Infinity }
          : { i: newId, x: 0, y: Infinity, w: 4, h: 6 };
        return {
          ...s,
          tabs: s.tabs.map((t) =>
            t.id === s.activeTabId
              ? { ...t, hidden: t.hidden.filter((id) => instanceTypeOf(id) !== widgetType), layout: [...t.layout, newItem] }
              : t
          ),
        };
      }

      // Singleton: classic toggle by id (id === type).
      const isOn = tab.layout.some((l) => l.i === widgetType);
      return {
        ...s,
        tabs: s.tabs.map((t) => {
          if (t.id !== s.activeTabId) return t;
          if (isOn) {
            return {
              ...t,
              hidden: t.hidden.includes(widgetType) ? t.hidden : [...t.hidden, widgetType],
              layout: t.layout.filter((l) => l.i !== widgetType),
            };
          }
          const newItem = def.defaultLayout
            ? { i: widgetType, ...def.defaultLayout, y: Infinity }
            : { i: widgetType, x: 0, y: Infinity, w: 4, h: 6 };
          return {
            ...t,
            hidden: t.hidden.filter((id) => id !== widgetType),
            layout: [...t.layout, newItem],
          };
        }),
      };
    });
  }, []);

  // Per-widget settings live on the active tab so each tab can have its own
  // filter/sort/timeframe per widget instance. Settings persist alongside the
  // layout — sharing a tab via the share-link includes its widget settings.
  const setWidgetSetting = useCallback((widgetId, key, value) => {
    setState((s) => ({
      ...s,
      tabs: s.tabs.map((t) => {
        if (t.id !== s.activeTabId) return t;
        const existing = t.widgetSettings?.[widgetId] || {};
        return {
          ...t,
          widgetSettings: {
            ...(t.widgetSettings || {}),
            [widgetId]: { ...existing, [key]: value },
          },
        };
      }),
    }));
  }, []);

  const resetActiveLayout = useCallback(() => {
    setState((s) => ({
      ...s,
      tabs: s.tabs.map((t) =>
        t.id === s.activeTabId ? { ...t, layout: buildDefaultLayout(), hidden: [] } : t
      ),
    }));
  }, []);

  const addTab = useCallback((preset = null) => {
    const newId = makeId();
    setState((s) => {
      const baseName = preset?.name || `Layout ${s.tabs.length + 1}`;
      // Suffix conflict resolution: "Foo" → "Foo (2)" if taken.
      let name = baseName;
      let suffix = 2;
      while (s.tabs.some((t) => t.name === name)) {
        name = `${baseName} (${suffix++})`;
      }
      const layout = preset?.layout || buildDefaultLayout();
      const hidden = preset?.hidden || [];
      // widgetSettings is keyed by layout `i`. Importing a preset (share-link,
      // duplicate, server-default fallback) keeps the same `i`s so the
      // settings remain addressable — restore them verbatim.
      const widgetSettings = preset?.widgetSettings || {};
      return {
        ...s,
        activeTabId: newId,
        tabs: [...s.tabs, { id: newId, name, layout, hidden, widgetSettings }],
      };
    });
    return newId;
  }, []);

  const closeTab = useCallback((id) => {
    setState((s) => {
      const target = s.tabs.find((t) => t.id === id);
      if (!target || target.pinned) return s;
      const remaining = s.tabs.filter((t) => t.id !== id);
      const tabs = remaining.length > 0 ? remaining : buildDefaultState().tabs;
      const activeTabId = s.activeTabId === id
        ? (tabs[0]?.id || DEFAULT_TAB_ID)
        : s.activeTabId;
      return { ...s, tabs, activeTabId };
    });
  }, []);

  const renameTab = useCallback((id, name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    setState((s) => ({
      ...s,
      tabs: s.tabs.map((t) => (t.id === id ? { ...t, name: trimmed } : t)),
    }));
  }, []);

  const reorderTabs = useCallback((fromIndex, toIndex) => {
    setState((s) => {
      if (fromIndex === toIndex) return s;
      const tabs = [...s.tabs];
      const [moved] = tabs.splice(fromIndex, 1);
      tabs.splice(toIndex, 0, moved);
      return { ...s, tabs };
    });
  }, []);

  const duplicateTab = useCallback((id) => {
    const source = stateRef.current.tabs.find((t) => t.id === id);
    if (!source) return null;
    // Deep-clone widgetSettings — addTab stores the reference, so a shallow
    // copy here would let edits on the duplicate bleed back into the source
    // tab (and vice-versa) through nested setting objects.
    return addTab({
      name: `${source.name} (copy)`,
      layout: source.layout.map((l) => ({ ...l })),
      hidden: [...source.hidden],
      widgetSettings: JSON.parse(JSON.stringify(source.widgetSettings || {})),
    });
  }, [addTab]);

  // Visibility = anything in the active layout (one entry per instance).
  // For multi widgets this means multiple ids share a type prefix.
  const visibleInstanceIds = useMemo(
    () => activeTab.layout.map((l) => l.i),
    [activeTab.layout]
  );
  // Counts per type — Library uses this to show how many instances of each
  // widget the active tab has.
  const visibleCountsByType = useMemo(() => {
    const counts = {};
    activeTab.layout.forEach((l) => {
      const type = instanceTypeOf(l.i);
      counts[type] = (counts[type] || 0) + 1;
    });
    return counts;
  }, [activeTab.layout]);
  const availableWidgetIds = ALL_WIDGET_IDS;

  return {
    tabs: state.tabs,
    activeTab,
    activeTabId: state.activeTabId,
    setActiveTab,
    addTab,
    closeTab,
    renameTab,
    reorderTabs,
    duplicateTab,
    updateActiveLayout,
    setHidden,
    hideWidget,
    showWidget,
    toggleWidget,
    resetActiveLayout,
    visibleInstanceIds,
    visibleCountsByType,
    availableWidgetIds,
    widgetSettings: activeTab.widgetSettings || {},
    setWidgetSetting,
  };
}
